import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Space, message, Select, Tabs, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import NameByLang from '../../Component/nameByLang';
import PermissionsTable from '../../Component/PermissionsTable';
// Styles imported from parent

const { TabPane } = Tabs;
const { Option } = Select;

interface UserModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  user: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const UserModal: React.FC<UserModalProps> = ({ visible, mode, user, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [currentPermissions, setCurrentPermissions] = useState<number[]>([]);
  const [isHydrated, setIsHydrated] = useState(false); // guard to prevent stale re-hydration
  const [rolesKey, setRolesKey] = useState(0); // Key to force Select re-render
  const [permissionsKey, setPermissionsKey] = useState(0); // Key to force PermissionsTable re-render
  const [currentFilterModels, setCurrentFilterModels] = useState<any[]>([]); // Track models for PermissionsTable filter (from API)

  // Fetch roles with their models
  const { data: rolesData } = useQuery({
    queryKey: ['/api/admin/roles'],
    queryFn: () => Api.get('/api/admin/roles'),
    enabled: visible,
  });

  const allRoles = rolesData?.data?.data?.data || rolesData?.data?.data || [];
  // Filter out the "user" role
  const roles = allRoles.filter((role: any) => role.name !== 'user');

  // Fetch user details if editing
  const { data: userDetails, refetch: refetchUserDetails } = useQuery({
    queryKey: [`/api/admin/users/${user?.id}`],
    queryFn: () => Api.get(`/api/admin/users/${user?.id}`),
    enabled: visible && mode === 'edit' && !!user?.id,
  });

  // Refetch user details when switching tabs (always refetch to get latest data)
  useEffect(() => {
    if (visible && mode === 'edit' && user?.id && isHydrated) {
      // Refetch whenever tab changes to ensure we have latest data after any updates
      // This ensures fresh data when switching tabs after adding/removing roles
      refetchUserDetails().catch((error) => {
        console.error('Error refetching user details:', error);
      });
    }
  }, [activeTab, visible, mode, user?.id, isHydrated, refetchUserDetails]);

  // Get models from currentFilterModels state (updated from API) - same as RoleModal
  const filterByModelsMemo = useMemo(() => {
    return currentFilterModels;
  }, [currentFilterModels]);

  // Get initial permissions from userDetails (similar to RoleModal using roleDetails)
  const initialPermissionsForTable = useMemo(() => {
    const userData = userDetails?.data?.data?.user || userDetails?.data?.user || null;
    return userData?.permissions || [];
  }, [userDetails]);

  const userDataForId = userDetails?.data?.data?.user || userDetails?.data?.user || null;
  const userId = userDataForId?.id || user?.id;

  const normalizeStatusToBoolean = (status: unknown): boolean => {
    if (status === undefined || status === null) {
      return true;
    }
    if (typeof status === 'string') {
      return ['active', '1', 'true', 'yes'].includes(status.toLowerCase().trim());
    }
    if (typeof status === 'number') {
      return status === 1;
    }
    return !!status;
  };

  const getModelsByRoleIds = (roleIds: number[], roleSource?: any[]) => {
    const models: any[] = [];
    const modelIds = new Set<number>();
    const rolesToUse = roleSource && roleSource.length > 0 ? roleSource : allRoles;

    roleIds.forEach((roleId) => {
      const role = rolesToUse.find((r: any) => r.id === roleId);
      const roleModels = role?.menu_models || role?.menuModels || [];
      roleModels.forEach((model: any) => {
        if (!modelIds.has(model.id)) {
          modelIds.add(model.id);
          models.push(model);
        }
      });
    });

    return models;
  };

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (data: any) => Api.post('/api/admin/users', data),
    onSuccess: () => {
      message.success(t('users.addSuccess', 'User created successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors;
        const msg = error?.response?.data?.msg;

        // Handle structured errors object
        if (errors && typeof errors === 'object') {
          Object.keys(errors).forEach((key) => {
            form.setFields([
              {
                name: key,
                errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
              },
            ]);
          });
        }
        // Show message if available (even with errors)
        if (msg) {
          message.error(msg);
        }
      } else {
        const errorMessage = error?.response?.data?.msg || t('common.error', 'Error creating user');
        message.error(errorMessage);
      }
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: (data: any) => {
      const userDataForId = userDetails?.data?.data?.user || userDetails?.data?.user || null;
      const currentUserId = userDataForId?.id || user?.id;
      if (!currentUserId) {
        return Promise.reject(new Error('User ID is not available'));
      }
      return Api.put(`/api/admin/users/${currentUserId}`, data);
    },
    onSuccess: () => {
      message.success(t('users.updateSuccess', 'User updated successfully'));
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors;
        const msg = error?.response?.data?.msg;

        // Handle structured errors object
        if (errors && typeof errors === 'object') {
          Object.keys(errors).forEach((key) => {
            form.setFields([
              {
                name: key,
                errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
              },
            ]);
          });
        }
        // Show message if available (even with errors)
        if (msg) {
          message.error(msg);
        }
      } else {
        const errorMessage = error?.response?.data?.msg || t('common.error', 'Error updating user');
        message.error(errorMessage);
      }
    },
  });

  // Assign roles mutation
  const assignRolesMutation = useMutation({
    mutationFn: (roleIds: number[]) => {
      const userDataForId = userDetails?.data?.data?.user || userDetails?.data?.user || null;
      const currentUserId = userDataForId?.id || user?.id;
      if (!currentUserId) {
        throw new Error('User ID is not available');
      }
      return Api.post(`/api/admin/users/${currentUserId}/assign-roles`, { roles: roleIds });
    },
    onSuccess: async () => {
      message.success(t('users.rolesAssigned', 'Roles assigned successfully'));

      // Refetch user details immediately to get updated roles from API
      try {
        const response = await refetchUserDetails();
        const userData = response?.data?.data?.user;
        if (userData) {
          const roleIds = userData.roles?.map((r: any) => r.id) || [];
          // Direct permissions are reset from the new role(s) on assign-roles
          const permissionIds =
            userData.permissions
              ?.map((p: any) => Number(p.id ?? p))
              .filter((id: number) => !Number.isNaN(id)) || [];

          const models = getModelsByRoleIds(roleIds, userData.roles);

          // Update selected roles for filtering permissions
          setSelectedRoleIds(roleIds);
          // Update current filter models for PermissionsTable (from API)
          setCurrentFilterModels(models);
          // Update current permissions - only direct permissions, not from roles
          setCurrentPermissions(permissionIds);

          // Update form values with fresh data from API
          form.setFieldsValue({
            roles: roleIds,
            permissions: permissionIds,
          });

          // Force Select re-render by incrementing key
          setRolesKey((k) => k + 1);
          // Also force PermissionsTable re-render since permissions changed with roles
          setPermissionsKey((k) => k + 1);
        }
      } catch (error) {
        console.error('Error refetching user details after role assignment:', error);
      }

      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      onSuccess?.();
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.msg || t('common.error', 'Error assigning roles');
      message.error(msg);
    },
  });

  // Assign permissions mutation
  const assignPermissionsMutation = useMutation({
    mutationFn: (permissionIds: number[]) => {
      const userDataForId = userDetails?.data?.data?.user || userDetails?.data?.user || null;
      const currentUserId = userDataForId?.id || user?.id;
      if (!currentUserId) {
        throw new Error('User ID is not available');
      }
      return Api.post(`/api/admin/users/${currentUserId}/assign-permissions`, {
        permissions: permissionIds,
      });
    },
    onSuccess: async () => {
      message.success(t('users.permissionsAssigned', 'Permissions assigned successfully'));

      // Refetch user details immediately to get updated permissions from API
      try {
        const response = await refetchUserDetails();
        const userData = response?.data?.data?.user;
        if (userData) {
          const roleIds = userData.roles?.map((r: any) => r.id) || [];
          // Use actual permissions from API, not recalculated from roles
          const permissionIds =
            userData.permissions
              ?.map((p: any) => Number(p.id ?? p))
              .filter((id: number) => !Number.isNaN(id)) || [];

          // Update selected roles for filtering permissions
          setSelectedRoleIds(roleIds);
          // Update current permissions with fresh data from API
          setCurrentPermissions(permissionIds);

          // Update form values with fresh data from API
          form.setFieldsValue({
            roles: roleIds,
            permissions: permissionIds,
          });

          // Force PermissionsTable re-render by incrementing key
          setPermissionsKey((k) => k + 1);
        }
      } catch (error) {
        console.error('Error refetching user details after permission assignment:', error);
      }

      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });

      // If updating current logged-in user's permissions, refresh user data in App.tsx
      // This ensures Dashboard and other pages see updated permissions immediately
      const userDataForId = userDetails?.data?.data?.user || userDetails?.data?.user || null;
      const currentUserId = userDataForId?.id || user?.id;
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Get current user ID from token or localStorage
          const currentUserResponse: any = await Api.get('/api/admin/me');
          if (currentUserResponse?.data?.user?.id === currentUserId) {
            // This is the current logged-in user - trigger a page refresh to update permissions
            window.location.reload();
          }
        } catch (error) {
          console.error('Error checking current user:', error);
        }
      }

      onSuccess?.();
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.msg || t('common.error', 'Error assigning permissions');
      message.error(msg);
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (mode === 'create') {
        await createUserMutation.mutateAsync(values);
      } else if (mode === 'edit') {
        await updateUserMutation.mutateAsync(values);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRolesSubmit = async () => {
    // Ensure we're on roles tab
    if (activeTab !== 'roles') return;

    // Get current user ID dynamically
    const currentUserId = userDetails?.data?.data?.user?.id || user?.id;
    if (!currentUserId) {
      message.error(t('users.userIdNotFound', 'User ID not found'));
      return;
    }

    try {
      // Validate only the roles field to avoid blocking on basic tab fields
      await form.validateFields(['roles']);
    } catch {
      message.error(t('common.validationError', 'Please fix validation errors'));
      return;
    }

    const roleIds = form.getFieldValue('roles') ?? [];

    // Validate that we have at least one role selected
    if (!Array.isArray(roleIds) || roleIds.length === 0) {
      message.warning(t('users.selectAtLeastOneRole', 'Please select at least one role'));
      return;
    }

    if (mode === 'edit') {
      setLoading(true);
      try {
        await assignRolesMutation.mutateAsync(roleIds);
      } catch (error) {
        // Error is handled by mutation's onError
        console.error('Error assigning roles:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePermissionsSubmit = async () => {
    // Ensure we're on permissions tab
    if (activeTab !== 'permissions') return;
    // Use currentPermissions state instead of form value
    const permissionIds = currentPermissions;
    if (mode === 'edit' && userId) {
      setLoading(true);
      try {
        await assignPermissionsMutation.mutateAsync(permissionIds);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePermissionsChange = (permissionIds: number[]) => {
    setCurrentPermissions(permissionIds);
    // Only update form if we're on permissions tab
    if (activeTab === 'permissions') {
      form.setFieldsValue({ permissions: permissionIds });
    }
  };

  // Handle roles change to update filtered permissions (same as RoleModal)
  const handleRolesChange = (roleIds: number[]) => {
    setSelectedRoleIds(roleIds);

    const models = getModelsByRoleIds(roleIds);

    // Update current filter models immediately (like RoleModal)
    setCurrentFilterModels(models);

    // DON'T auto-add role permissions - only keep current direct selections
    // Role permissions are only used for filtering, not for auto-selection
    // User must explicitly select permissions they want
    const permissionIds = currentPermissions; // Keep only current selections

    // Update form values
    form.setFieldsValue({ roles: roleIds, permissions: permissionIds });
  };

  // Update form fields when switching tabs to show current data
  const handleTabChange = (activeKey: string) => {
    setActiveTab(activeKey);

    // Re-populate roles/permissions when switching to those tabs
    if (activeKey === 'roles' || activeKey === 'permissions') {
      // Prefer current state (already updated on save) to avoid reverting to stale user prop
      const roleIds = selectedRoleIds.length
        ? selectedRoleIds
        : (() => {
            const userDataForRoles =
              userDetails?.data?.data?.user || userDetails?.data?.user || null;
            return (
              userDataForRoles?.roles?.map((r: any) => r.id) ||
              user?.roles?.map((r: any) => r.id) ||
              []
            );
          })();
      // Only use direct permissions - don't auto-add role permissions
      const directPermissionIds = (() => {
        const userDataForPerms = userDetails?.data?.data?.user || userDetails?.data?.user || null;
        return (
          userDataForPerms?.permissions?.map((p: any) => p.id) ||
          user?.permissions?.map((p: any) => p.id) ||
          []
        );
      })();
      const permissionIds = currentPermissions.length ? currentPermissions : directPermissionIds; // Only direct permissions, not from roles
      const models = getModelsByRoleIds(roleIds);

      setSelectedRoleIds(roleIds);
      setCurrentPermissions(permissionIds);
      setCurrentFilterModels(models);
      form.setFieldsValue({
        roles: roleIds,
        permissions: permissionIds,
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setActiveTab('basic');
    setSelectedRoleIds([]);
    setCurrentPermissions([]);
    setCurrentFilterModels([]);
    setIsHydrated(false);
    onClose();
  };

  // Update models and permissions when userDetails changes (similar to RoleModal)
  useEffect(() => {
    if (!visible || mode !== 'edit') {
      return;
    }

    // Wait for userDetails to be loaded
    // Check different possible response structures
    const userData = userDetails?.data?.data?.user || userDetails?.data?.user || null;

    if (!userData) {
      // If userDetails is not loaded yet, reset form to prevent stale data
      if (isHydrated) {
        form.resetFields();
        setIsHydrated(false);
      }
      return;
    }
    const roleIds = userData.roles?.map((r: any) => r.id) || [];
    // Only use direct permissions - don't auto-add role permissions
    const directPermissionIds =
      userData.permissions
        ?.map((p: any) => Number(p.id ?? p))
        .filter((id: number) => !Number.isNaN(id)) || [];
    const permissionIds = directPermissionIds;

    const models = getModelsByRoleIds(roleIds, userData.roles);

    // Update current filter models for PermissionsTable (from API)
    setCurrentFilterModels(models);
    // Update selected roles
    setSelectedRoleIds(roleIds);
    // Update current permissions for submission
    setCurrentPermissions(permissionIds);
    setPermissionsKey((k) => k + 1);

    const baseFormValues = {
      name: userData.name || '',
      phone_number: userData.phone_number || '',
      password: undefined,
      locale: userData.locale || 'ar',
      status: normalizeStatusToBoolean(userData.status),
      roles: roleIds,
      permissions: permissionIds,
    };

    // Always hydrate all fields when userDetails is loaded
    if (!isHydrated) {
      // Set all form values including basic info
      // Use setTimeout to ensure Form element is mounted in DOM
      const timer = setTimeout(() => {
        // Check if form is connected to a Form element
        if (form && typeof form.setFieldsValue === 'function') {
          form.setFieldsValue(baseFormValues);
          setIsHydrated(true);
        }
      }, 50);

      // Cleanup timer on unmount
      return () => clearTimeout(timer);
    } else {
      // Update form values based on active tab
      if (activeTab === 'permissions' || activeTab === 'roles') {
        try {
          form.setFieldsValue({
            roles: roleIds,
            permissions: permissionIds,
          });
        } catch (error) {
          console.error('Error updating roles/permissions:', error);
        }
      } else if (activeTab === 'basic') {
        // Always update basic info fields when on basic tab
        try {
          form.setFieldsValue({
            name: userData.name || '',
            phone_number: userData.phone_number || '',
            password: undefined,
            locale: userData.locale || 'ar',
            status: normalizeStatusToBoolean(userData.status),
          });
        } catch (error) {
          console.error('Error updating basic info:', error);
        }
      }
    }
  }, [userDetails, activeTab, visible, mode, isHydrated, form]);

  // Set form values when editing (initial load)
  useEffect(() => {
    if (!visible) {
      setIsHydrated(false);
      setCurrentFilterModels([]);
      setSelectedRoleIds([]);
      setCurrentPermissions([]);
      form.resetFields();
      return;
    }

    if (visible && mode === 'create') {
      form.resetFields();
      setSelectedRoleIds([]);
      setCurrentPermissions([]);
      setCurrentFilterModels([]);
      setIsHydrated(true);
      form.setFieldsValue({
        locale: 'ar',
        status: true,
      });
    } else if (visible && mode === 'edit' && user?.id) {
      // Reset form when opening edit modal - wait for userDetails to load
      form.resetFields();
      setIsHydrated(false);
      // Clear any previous values to prevent stale data
      setSelectedRoleIds([]);
      setCurrentPermissions([]);
      setCurrentFilterModels([]);
    }
  }, [visible, mode, user?.id, form]);

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return t('users.addNew', 'Add New User');
      case 'edit':
        return t('users.edit', 'Edit User');
      default:
        return '';
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1400}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab={t('users.basicInfo', 'Basic Information')} key="basic">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              name: '',
              phone_number: '',
              password: undefined,
              locale: 'ar',
              status: true,
            }}
          >
            <Form.Item
              name="name"
              label={t('users.form.name', 'Name')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input placeholder={t('users.form.namePlaceholder', 'Enter user name')} />
            </Form.Item>

            <Form.Item
              name="phone_number"
              label={t('users.form.phone', 'Phone Number')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input placeholder={t('users.form.phonePlaceholder', 'Enter phone number')} />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('users.form.password', 'Password')}
              rules={[
                mode === 'create'
                  ? { required: true, message: t('validation.required', 'This field is required') }
                  : {},
                {
                  min: 6,
                  message: t('validation.passwordMin', 'Password must be at least 6 characters'),
                },
              ]}
            >
              <Input.Password placeholder={t('users.form.passwordPlaceholder', 'Enter password')} />
            </Form.Item>

            <Form.Item name="locale" label={t('users.form.locale', 'Locale')}>
              <Select>
                <Option value="ar">{t('common.arabic', 'Arabic')}</Option>
                <Option value="en">{t('common.english', 'English')}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label={t('users.form.status', 'Status')}
              valuePropName="checked"
            >
              <Switch
                checkedChildren={t('common.active', 'Active')}
                unCheckedChildren={t('common.inactive', 'Inactive')}
              />
            </Form.Item>

            {mode === 'create' && (
              <Form.Item name="roles" label={t('users.form.roles', 'Roles')}>
                <Select mode="multiple" placeholder={t('users.form.selectRoles', 'Select roles')}>
                  {roles.map((role: any) => (
                    <Option key={role.id} value={role.id}>
                      <NameByLang
                        name_en={role.display_name_en}
                        name_ar={role.display_name_ar}
                        name={role.display_name || role.name}
                      />
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {mode === 'create' ? t('common.add', 'Add') : t('common.update', 'Update')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        {mode === 'edit' && (
          <>
            <TabPane tab={t('users.roles', 'Roles')} key="roles">
              <Form form={form} layout="vertical">
                <Form.Item
                  name="roles"
                  label={t('users.form.assignRoles', 'Assign Roles')}
                  rules={[
                    {
                      required: true,
                      message: t('users.selectAtLeastOneRole', 'Please select at least one role'),
                    },
                  ]}
                >
                  <Select
                    key={`roles-select-${rolesKey}`}
                    mode="multiple"
                    placeholder={t('users.form.selectRoles', 'Select roles')}
                    style={{ width: '100%' }}
                    onChange={handleRolesChange}
                  >
                    {roles.map((role: any) => (
                      <Option key={role.id} value={role.id}>
                        <NameByLang
                          name_en={role.display_name_en}
                          name_ar={role.display_name_ar}
                          name={role.display_name || role.name}
                        />
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                    <Button type="primary" onClick={handleRolesSubmit} loading={loading}>
                      {t('users.assignRoles', 'Assign Roles')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab={t('users.permissions', 'Permissions')} key="permissions">
              <Form form={form} layout="vertical">
                <Form.Item
                  name="permissions"
                  label={t('users.form.assignPermissions', 'Assign Permissions')}
                >
                  <PermissionsTable
                    key={`permissions-${user?.id}-${selectedRoleIds.join('-')}-${permissionsKey}-${filterByModelsMemo.length}-${currentPermissions.join('-')}`}
                    selectedPermissions={currentPermissions}
                    onChange={handlePermissionsChange}
                    initialPermissions={initialPermissionsForTable}
                    filterByModels={filterByModelsMemo}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                    <Button type="primary" onClick={handlePermissionsSubmit} loading={loading}>
                      {t('users.assignPermissions', 'Assign Permissions')}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>
          </>
        )}
      </Tabs>
    </Modal>
  );
};

export default UserModal;
